import { useState, useEffect, useMemo, ReactElement, ReactNode } from 'react'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Table, useMagneticTable, MdsTableColumn, getSelectionColumn } from '@magnetic/table'
import { FilterBar } from '@magnetic/filter-bar'
import { Search } from '@magnetic/search'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Tag } from '@magnetic/tag'
import { Spinner } from '@magnetic/spinner'
import { Monitor, XCircle } from '@/lib/icons'
import { timestampToDate } from '@/lib/protobuf-utils'
import type { ListActiveSessionsResponse, SessionInfo } from '@/gen/catpocs/v1/admin_pb'
import type { listActiveSessions } from '@/lib/api'

interface SessionRow {
  sessionId: string
  userId: string
  email: string
  name: string
  createdAt: string
  lastActivity: string
  expiresAt: string
  isActive: boolean
  isImpersonating: boolean
  impersonatedEmail?: string
}

interface ActiveSessionsTableProps {
  queryFn: typeof listActiveSessions
  title?: string
  description?: string
  selectableRows?: boolean
  configComponents?: ReactElement | null
  bulkActions?: (selectedCount: number, selectedRows: SessionRow[]) => ReactNode
  renderActions?: (session: SessionRow) => ReactNode
  initialPageSize?: number
  autoRefreshSeconds?: number
  userId?: string // Optional: filter by specific user
}

export function ActiveSessionsTable({
  queryFn,
  title = 'Active Sessions',
  description,
  selectableRows = false,
  configComponents,
  bulkActions,
  renderActions,
  initialPageSize = 20,
  autoRefreshSeconds = 30,
  userId,
}: ActiveSessionsTableProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(0)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setCurrentPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch data with all filters
  const pageSize = initialPageSize
  const skip = currentPage * pageSize
  const { data: rawData, isLoading, error } = useConnectQuery(
    queryFn,
    {
      userId: userId || '',
      search: debouncedSearch,
      pageSize: pageSize,
      skip: skip,
    },
    {
      refetchInterval: autoRefreshSeconds * 1000, // Auto-refresh
    }
  )

  const data = rawData as ListActiveSessionsResponse | undefined
  const totalCount = data?.totalCount ?? 0
  const sessions: SessionInfo[] = useMemo(() => data?.sessions ?? [], [data?.sessions])

  // Transform data for table
  const tableData: SessionRow[] = useMemo(() => {
    if (sessions.length === 0) return []
    return sessions.map((session) => ({
      sessionId: session.sessionId || '',
      userId: session.userId || '',
      email: session.email || '',
      name: session.name || '',
      createdAt: session.createdAt ? (timestampToDate(session.createdAt)?.toLocaleString() || 'Invalid Date') : '',
      lastActivity: session.lastActivity ? (timestampToDate(session.lastActivity)?.toLocaleString() || 'Invalid Date') : '',
      expiresAt: session.expiresAt ? (timestampToDate(session.expiresAt)?.toLocaleString() || 'Invalid Date') : '',
      isActive: session.isActive || false,
      isImpersonating: session.isImpersonating || false,
      impersonatedEmail: session.impersonatedEmail || undefined,
    }))
  }, [sessions])

  // Define columns
  const columns: MdsTableColumn<SessionRow>[] = useMemo(() => {
    const cols: MdsTableColumn<SessionRow>[] = [
      {
        accessorKey: 'name',
        header: 'User',
        cell: (info) => (
          <Flex direction="vertical" gap={2}>
            <Text size="p3" weight="semi-bold">{info.getValue() as string}</Text>
            <Text size="p4" color="light">{info.row.original.email}</Text>
            {info.row.original.isImpersonating && (
              <Tag color="orange">
                Impersonating: {info.row.original.impersonatedEmail}
              </Tag>
            )}
          </Flex>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Started',
        cell: (info) => <Text size="p3">{info.getValue() as string}</Text>,
      },
      {
        accessorKey: 'lastActivity',
        header: 'Last Activity',
        cell: (info) => <Text size="p3">{info.getValue() as string}</Text>,
      },
      {
        accessorKey: 'expiresAt',
        header: 'Expires',
        cell: (info) => <Text size="p3">{info.getValue() as string}</Text>,
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: (info) => {
          const isActive = info.getValue() as boolean
          return (
            <Text size="p3" weight="semi-bold" style={{
              color: isActive ? 'var(--mds-color-theme-text-positive-normal)' : 'var(--mds-color-theme-text-normal)'
            }}>
              {isActive ? 'Active' : 'Inactive'}
            </Text>
          )
        },
      },
    ]

    if (renderActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        cell: (info) => renderActions(info.row.original),
      })
    }

    if (selectableRows) {
      return [getSelectionColumn<SessionRow>(), ...cols]
    }
    return cols
  }, [selectableRows, renderActions])

  // Use Magnetic table hook
  const { table } = useMagneticTable({
    data: tableData,
    columns,
  })

  const showReset = debouncedSearch !== ''
  const handleReset = () => {
    setSearchTerm('')
    setDebouncedSearch('')
    setCurrentPage(0)
  }

  if (error) {
    return (
      <Flex direction="vertical" align="center" gap={16} style={{ padding: 48 }}>
        <XCircle size={48} />
        <Heading size="sub-section">Failed to load sessions</Heading>
        <Text color="light">{error instanceof Error ? error.message : 'An error occurred'}</Text>
      </Flex>
    )
  }

  return (
    <Flex direction="vertical" gap={16} style={{ width: '100%' }}>
      {(title || description) && (
        <Flex direction="vertical" gap={4}>
          {title && <Heading size="h2">{title}</Heading>}
          {description && <Text color="light">{description}</Text>}
        </Flex>
      )}

      <FilterBar
        searchBar={
          <Search
            label="Search sessions"
            placeholder="Search by user name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        }
        results={totalCount}
        isLoadingResults={isLoading}
        showResetButton={showReset}
        onReset={handleReset}
        configComponents={configComponents ?? undefined}
      >
        <Text size="p4" color="light">
          Auto-refreshes every {autoRefreshSeconds}s
        </Text>
      </FilterBar>

      {selectableRows && bulkActions && table.getSelectedRowModel().rows.length > 0 && (
        <Table.BulkActionBar>
          <Flex align="center" gap={12}>
            {bulkActions(
              table.getSelectedRowModel().rows.length,
              table.getSelectedRowModel().rows.map((row) => row.original)
            )}
          </Flex>
        </Table.BulkActionBar>
      )}

      {isLoading ? (
        <Flex align="center" justify="center" style={{ padding: 48 }}>
          <Spinner size="lg" />
        </Flex>
      ) : tableData.length === 0 ? (
        <Flex direction="vertical" align="center" gap={16} style={{ padding: 48 }}>
          <Monitor size={48} style={{ opacity: 0.3 }} />
          <Heading size="sub-section">No active sessions</Heading>
          <Text color="light">
            {debouncedSearch ? 'Try adjusting your search' : 'No users are currently signed in'}
          </Text>
        </Flex>
      ) : (
        <Table
          table={table}
          paginationControls={totalCount > pageSize ? {} : undefined}
        />
      )}
    </Flex>
  )
}
